import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  params, string, arrayOf,
  shape, useDataModel, extend, bool
} from '@thd-nucleus/data-sources';
import {
  Button, DrawerBody,
  DrawerFooter, Typography,
  Skeleton, SkeletonLine,
  SkeletonBlock, Link,
  Accordion, AccordionHeader, AccordionBody
} from '@one-thd/sui-atomic-components';
import { Carousel } from '@one-thd/sui-carousel';
import { useStore } from '@thd-nucleus/experience-context';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { Price } from '@thd-olt-component-react/price';
import { ProductOverviewMobile as ProductOverview } from '@thd-olt-component-react/product-overview';
import {
  RatingsAndReviewsCondensed,
  RatingsAndReviewsAccordionTitle
} from '@thd-olt-component-react/ratings-and-reviews';
import { Specifications } from '@thd-olt-component-react/specifications';
import { Quantity } from '@thd-olt-component-react/quantity';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { SalientPoints, SalientListItem } from '@thd-olt-component-react/salient-points';
import { AddToCart as AddToCartButton } from '@thd-olt-component-react/add-to-cart';
import { AddToContext } from '../../AddToContext';
import { triggerAnalytics } from '../../AddToHelpers';
import { SCREENS, ANALYTICS_SELECT_PROJECT } from '../../AddToConstants';
import { AddToLink } from './links/AddToLink';
import { ProductRating } from '@thd-olt-component-react/product-pod';

export const MiniPIPFull = ({ itemId }) => {
  const store = useStore({ varnish: false, online: false });
  const { storeId } = store;
  const { setScreen, addToType, setSelectedProductDetails, setAtcDrawerType, onATCClick } = useContext(AddToContext);
  const {
    MINI_PIP_PRODUCT_OVERVIEW,
    PROJECT_SELECT_PROJECT
  } = SCREENS;

  const { data, loading } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: !itemId
  });

  const { product } = data || {};
  const images = product?.media?.images || [];
  const brandName = product?.identifiers?.brandName || '';
  const productLabel = product?.identifiers?.productLabel || '';
  const [quantity, setQuantity] = useState(1);
  const setImageDisplaySize = (url) => url?.replace(/<SIZE>/g, '400');

  const onQuantityChange = (item, value) => {
    const quant = value?.quantity;
    setQuantity(quant);
  };

  const onChange = (changeData = {}) => {
    const { canonicalUrl, itemId } = changeData;
    if (canonicalUrl) {
      setSelectedProductDetails({ itemId, quantity, productData: product });
    }
  };

  const handleBttnClick = (event) => {
    const details = product.specificationGroup.find((group) => group.specTitle === 'Details');
    const applianceType = details.specifications.find((spec) => spec.specName === 'Appliance Type')?.specValue;
    window.LIFE_CYCLE_EVENT_BUS.trigger('quick-view.click-footer',
      {
        productType: product?.identifiers?.productType,
        isLaundryBundle: applianceType?.includes('Washer')
          || applianceType?.includes('Dryer')
          || applianceType?.includes('Pedestal')
          || applianceType?.includes('Part/Accessory')
      }
    );
  };

  const addToContainerClasses = classNames(
    'sui-w-full',
    'sui-inline-flex',
    'sui-items-center',
    'sui-justify-end',
    'sui-gap-4',
    'sui-pt-2',
  );

  if (loading) {
    return (
      <DrawerBody>
        <SkeletonLine />
        <Skeleton grow>
          <div className="sui-w-full sui-h-full sui-px-9">
            <SkeletonBlock width={384} height={384} />
          </div>
        </Skeleton>
        <SkeletonLine variant="multi" numberOfLines={4} />
      </DrawerBody>
    );
  }

  const breakpoints = {
    sm: {
      slidesPerView: 2,
      slidesPerGroup: 2
    },
    md: {
      slidesPerView: 2,
      slidesPerGroup: 2
    },
    lg: {
      slidesPerView: 2,
      slidesPerGroup: 2
    },
    xl: {
      slidesPerView: 2,
      slidesPerGroup: 2
    },
    '2xl': {
      slidesPerView: 2,
      slidesPerGroup: 2
    }
  };

  return (
    <>
      <div className="sui-bg-primary sui-px-4 sui-pt-2 sui-shrink sui-basis-auto sui-grow">
        <div className="sui-flex sui-flex-col sui-gap-6">
          <div className="sui-flex sui-flex-col sui-gap-2">
            <Typography variant="h5" data-testid="test-brandName">{brandName}</Typography>
            <Typography data-testid="test-productLabel">{productLabel}</Typography>
            <ProductRating itemId={itemId} />
          </div>
          <div id="mini-pip-full-carousel-container">
            <Carousel
              containerId="mini-pip-full-carousel-container" 
              breakpoints={breakpoints}
              disableMargin
              layout="full-bleed"
              type="peek"
              peek="md"
            >
              {images.map((image, index) => (
                <div key={`hero-image-${index}`}>
                  <img
                    src={setImageDisplaySize(image.url)}
                    alt={productLabel}
                    width={268}
                    height={268}
                    title={productLabel}
                    loading="lazy"
                  />
                </div>
              ))}
            </Carousel>
          </div>
          <SuperSku itemId={itemId} onChange={onChange} disableMediaAndAvailability />
          <SalientPoints itemId={itemId}>
            <SalientListItem>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                underline="always"
                onClick={() => setScreen(MINI_PIP_PRODUCT_OVERVIEW)}
                data-testid="view-more"
                component="button"
              >
                View More Details
              </Link>
            </SalientListItem>
          </SalientPoints>
          <Price
            itemId={itemId}
            large={false}
            storeId={storeId}
            displayEachUom={false}
            showPreferredPricingBadge
            hideForProductTypes={['HDQC']}
          />
          <div className="sui-flex sui-flex-col">
            <FulfillmentPodStore itemId={itemId} storeId={storeId} hideForChooseYourOptions />
            <FulfillmentPodShipping itemId={itemId} storeId={storeId} />
          </div>
        </div>
        <hr className="sui-mt-6 sui-mb-0" />
        <div>
          <Accordion noBorders>
            <AccordionHeader aria-controls="product-info-overview" id="product-info-overview">
              Product Details
            </AccordionHeader>
            <AccordionBody>
              <div className="sui-pb-4">
                <ProductOverview itemId={itemId} isMiniPip />
              </div>
            </AccordionBody>
          </Accordion>
          <hr className="sui-mb-1" />
          <Accordion noBorders>
            <AccordionHeader aria-controls="product-info-specifications" id="product-info-specifications">
              Specifications
            </AccordionHeader>
            <AccordionBody>
              <Specifications itemId={itemId} hideMobileFade oneColumn hideSpecificationHeader />
            </AccordionBody>
          </Accordion>
          <hr className="sui-mb-1" />
          <Accordion noBorders>
            <AccordionHeader aria-controls="product-info-reviews" id="product-info-reviews">
              <RatingsAndReviewsAccordionTitle
                noSpacing
                itemId={itemId}
              />
            </AccordionHeader>
            <AccordionBody>
              <RatingsAndReviewsCondensed itemId={itemId} />
            </AccordionBody>
          </Accordion>
        </div>
      </div>
      <DrawerFooter position="center">
        <div className="sui-flex sui-flex-col sui-gap-2">
          <Button
            onClick={handleBttnClick}
            fullWidth
            tag="a"
            href={product?.identifiers?.canonicalUrl}
            variant="secondary"
          >
            View Full Details
          </Button>
          <div className="sui-flex sui-w-full sui-flex-row sui-gap-4">
            <div className="sui-w-full">
              <Quantity
                value={quantity}
                onChange={(event) => onQuantityChange(itemId, event)}
                wideView
              />
            </div>
            <div className="sui-w-full">
              <AddToCartButton
                cartReqParams={{ itemId, quantity }}
                fullWidth
                onClick={onATCClick}
              >
                Add to Cart
              </AddToCartButton>
            </div>
          </div>
          <div className={addToContainerClasses}>
            <Typography weight="bold">Add to:</Typography>
            <AddToLink text="List" onClick={() => {
              setScreen('LIST_SELECT_LIST');
              setAtcDrawerType('List');
            }}/>
            <AddToLink text="Project" onClick={() => {
              setScreen(PROJECT_SELECT_PROJECT);
              setAtcDrawerType('Project');
              triggerAnalytics(ANALYTICS_SELECT_PROJECT, {});
            }}/>
          </div>
        </div>
      </DrawerFooter>
    </>
  );
};

MiniPIPFull.displayName = 'MiniPIPFull';

MiniPIPFull.propTypes = {
  itemId: PropTypes.string.isRequired,
};

MiniPIPFull.defaultProps = {};

MiniPIPFull.dataModel = extend(
  {
    product: params({
      itemId: string().isRequired(),
      dataSource: string()
    }).shape({
      itemId: string(),
      dataSources: string(),
      info: shape({
        isBrioSku: bool().client(),
        isCustomKitchenCabinet: bool().client()
      }),
      identifiers: shape({
        brandName: string(),
        productLabel: string(),
        storeSkuNumber: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      }),
      reviews: shape({
        ratingsReviews: shape({
          averageRating: string(),
          totalReviews: string()
        })
      })
    })
  },
  Price,
  SuperSku,
  SalientPoints,
  ProductRating,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  RatingsAndReviewsCondensed,
  RatingsAndReviewsAccordionTitle
);
